import React, { useEffect, useMemo, useState } from 'react';
import { Ship, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { KpiCard } from '../../components/ui/KpiCard';
import { Badge } from '../../components/ui/Badge';
import { Table } from '../../components/ui/Table';
import { syncTimestampColumns } from '../../components/ui/SyncTimestamps';
import { useTranslation } from 'react-i18next';
import { SectionCard } from '../../components/ui/SectionCard';
import { BarChart } from '../../components/ui/BarChart';
import api from '../../services/api';
import { SHIPMENT_STATUS, fmtDate, groupByMonth } from '../../utils/dashboardUtils';
import { DashHeader, DashError, StatusBreakdown, MiniEmpty } from './DashboardShared';

const TRANSIT_COLS = [
  {
    key: 'containerRef',
    label: 'Réf.',
    render: (v) => <span className="font-mono text-[12px] font-medium text-primary">{v || '—'}</span>,
  },
  {
    key: 'productName',
    label: 'Produit',
    render: (v) => <span className="font-medium text-slate-700">{v}</span>,
  },
  {
    key: 'route',
    label: 'Route',
    render: (_, row) => (
      <span className="text-slate-500 text-[12px]">{row.departure} → {row.arrival}</span>
    ),
  },
  {
    key: 'etd',
    label: 'ETD',
    render: (v) => <span className="text-slate-500 text-[12px]">{v}</span>,
  },
  {
    key: 'statusVariant',
    label: 'Statut',
    render: (v, row) => <Badge variant={v}>{row.statusLabel}</Badge>,
  },
];

const INCIDENT_COLS = [
  {
    key: 'containerRef',
    label: 'Réf.',
    render: (v) => <span className="font-mono text-[12px] font-medium text-primary">{v || '—'}</span>,
  },
  {
    key: 'productName',
    label: 'Produit',
    render: (v) => <span className="font-medium text-slate-700">{v}</span>,
  },
  {
    key: 'incidentDesc',
    label: 'Description',
    render: (v) => (
      <span className="text-rose-700 text-[12px] line-clamp-1">{v || 'Incident signalé'}</span>
    ),
  },
  {
    key: 'statusVariant',
    label: 'Statut',
    render: (v, row) => <Badge variant={v}>{row.statusLabel}</Badge>,
  },
];

export default function OperationsDashboard() {
  const { t, i18n } = useTranslation();
  const timestampColumns = syncTimestampColumns(t, i18n.language);
  const { user } = useAuth();
  const [shipments, setShipments] = useState([]);
  const [orders, setOrders]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);

  function load() {
    setLoading(true);
    setError(null);
    Promise.all([api.get('/shipments'), api.get('/orders')])
      .then(([sRes, oRes]) => { setShipments(Array.isArray(sRes.data) ? sRes.data : (sRes.data?.data ?? [])); setOrders(oRes.data); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, []);

  const enriched = useMemo(() => {
    const orderMap = {};
    orders.forEach((o) => { orderMap[o._id] = o; });
    return shipments.map((s) => {
      const order = orderMap[s.orderId] || {};
      const mapped = SHIPMENT_STATUS[s.status] || { variant: 'info', label: s.status || '—', group: 'other' };
      return {
        ...s,
        statusVariant: mapped.variant,
        statusLabel:   mapped.label,
        statusGroup:   mapped.group,
        productName:   order.productName || '—',
        departure:     order.origin?.city || order.origin?.portCode || '—',
        arrival:       order.destination?.finalDestinationCode || order.destination?.portCode || '—',
        etd:           fmtDate(order.etd),
        incidentDesc:  s.incident?.description || null,
      };
    });
  }, [shipments, orders]);

  const kpis = useMemo(() => ({
    total:     enriched.length,
    inTransit: enriched.filter((s) => s.statusGroup === 'transit').length,
    delivered: enriched.filter((s) => s.statusGroup === 'delivered').length,
    incidents: enriched.filter((s) => s.statusGroup === 'incident').length,
  }), [enriched]);

  const monthlyData  = useMemo(() => groupByMonth(shipments, 'createdAt'), [shipments]);
  const transitRows  = useMemo(() => enriched.filter((s) => s.statusGroup === 'transit').slice(0, 5), [enriched]);
  const incidentRows = useMemo(() => enriched.filter((s) => s.statusGroup === 'incident').slice(0, 5), [enriched]);

  const statusItems = [
    { label: 'En transit', count: kpis.inTransit, variant: 'transit'   },
    { label: 'Livrées',    count: kpis.delivered, variant: 'delivered' },
    { label: 'Incidents',  count: kpis.incidents, variant: 'incident'  },
  ];

  return (
    <div className="flex flex-col gap-5">
      <DashHeader
        title="Tableau de bord — Opérations"
        sub={`Bonjour ${user?.firstName} — ${enriched.length} expédition${enriched.length !== 1 ? 's' : ''} au total`}
        onRefresh={load}
        loading={loading}
      />

      {error && <DashError message={error} onRetry={load} />}

      <div className="grid grid-cols-4 gap-4">
        <KpiCard
          label="Total expéditions"
          value={loading ? '—' : kpis.total}
          icon={Ship}
          sparkline={!loading && shipments.length ? monthlyData.map((d) => d.value) : undefined}
        />
        <KpiCard
          label="En transit"
          value={loading ? '—' : kpis.inTransit}
          sub={!loading && kpis.total ? `${Math.round((kpis.inTransit / kpis.total) * 100)} % du total` : undefined}
        />
        <KpiCard
          label="Livrées"
          value={loading ? '—' : kpis.delivered}
          sub={!loading && kpis.total ? `${Math.round((kpis.delivered / kpis.total) * 100)} % du total` : undefined}
        />
        <KpiCard
          label="Incidents actifs"
          value={loading ? '—' : kpis.incidents}
          icon={AlertTriangle}
          trend={kpis.incidents > 0 ? 'down' : undefined}
          trendLabel={kpis.incidents > 0 ? `${kpis.incidents} à traiter` : 'Aucun incident'}
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <SectionCard title="Expéditions par mois" className="col-span-2">
          {!loading && <BarChart data={monthlyData} />}
          {loading && <div className="h-[68px] bg-slate-100 rounded animate-pulse" />}
        </SectionCard>
        <SectionCard title="Répartition par statut">
          {loading
            ? <div className="space-y-2.5">{[1, 2, 3].map((i) => <div key={i} className="h-4 bg-slate-100 rounded animate-pulse" />)}</div>
            : <StatusBreakdown items={statusItems} total={kpis.total} />
          }
        </SectionCard>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <SectionCard
          title="Expéditions en transit"
          action={
            <span className="text-[11px] font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
              {transitRows.length}
            </span>
          }
        >
          {transitRows.length > 0
            ? <Table columns={[...TRANSIT_COLS, ...timestampColumns]} rows={transitRows} rowKey="_id" />
            : <MiniEmpty label="Aucune expédition en transit" />
          }
        </SectionCard>

        <SectionCard
          title="Incidents actifs"
          action={
            incidentRows.length > 0 && (
              <span className="text-[11px] font-semibold text-rose-600 bg-rose-50 px-2 py-0.5 rounded-full">
                {incidentRows.length}
              </span>
            )
          }
        >
          {incidentRows.length > 0
            ? <Table columns={[...INCIDENT_COLS, ...timestampColumns]} rows={incidentRows} rowKey="_id" />
            : <MiniEmpty label="Aucun incident actif" ok />
          }
        </SectionCard>
      </div>
    </div>
  );
}
